// app/_components/conditions/SafetyHeadsUp.tsx
"use client";

import { useShallowSearchParam } from "@/lib/hooks/use-shallow-search-param";
import { resolveSlotSport } from "@/lib/conditions/sport-aware-slot";
import {
  resolveSafety,
  type SafetyHeadsUpInput,
  type SafetySeverity,
} from "@/lib/conditions/safety-headsup";

const CLEAR_TONE = "bg-[#EEF3EC] border-[#cfddc8] text-[#3f6b32]";
const NO_DATA_TONE = "bg-[#F6F1E7] border-[#d9cab0] text-[#725a22]";
const FLAG_TONE: Record<SafetySeverity, string> = {
  none: CLEAR_TONE,
  info: NO_DATA_TONE,
  caution: "bg-amber-50 border-amber-300 text-amber-800",
  danger: "bg-red-50 border-red-300 text-red-800",
};

export function SafetyHeadsUp({
  defaultSport,
  perSport,
  children,
  availableSports,
}: {
  defaultSport: string;
  perSport: Record<string, SafetyHeadsUpInput>;
  children?: React.ReactNode;
  availableSports?: readonly string[];
}) {
  const urlSport = useShallowSearchParam("sport") ?? defaultSport;
  const activeSport = resolveSlotSport(
    urlSport,
    defaultSport,
    availableSports ?? Object.keys(perSport),
  );
  const model = resolveSafety(perSport[activeSport]);

  return (
    <section className="mt-3">
      {model.state === "clear" ? (
        <div className={`rounded-[12px] border px-3.5 py-2.5 text-[12.5px] font-semibold ${CLEAR_TONE}`}>
          ✓ {model.headline}
        </div>
      ) : model.state === "no-data" ? (
        <div className={`rounded-[12px] border px-3.5 py-2.5 text-[12.5px] font-semibold ${NO_DATA_TONE}`}>
          {model.headline}
        </div>
      ) : (
        <details open={model.autoExpand} className={`group rounded-[12px] border px-3.5 py-2.5 ${FLAG_TONE[model.severity]}`}>
          <summary className="min-h-11 text-[12.5px] font-semibold cursor-pointer list-none flex items-center gap-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal">
            <span>⚠ {model.headline}</span>
            <span aria-hidden="true" className="ml-auto group-open:hidden">＋</span>
            <span aria-hidden="true" className="ml-auto hidden group-open:inline">－</span>
          </summary>
          <ul className="mt-2 space-y-2 text-[12px] leading-relaxed">
            {perSport[activeSport]?.hazards.filter((hazard) => hazard.severity !== "none" && hazard.label !== model.headline).map((hazard) => <li key={hazard.label}>{hazard.label}</li>)}
          </ul>
          {children && <div className="mt-2">{children}</div>}
        </details>
      )}
    </section>
  );
}
